import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as yup from "yup";
import { icons } from "../../../icons";
import { HeadLine3 } from "../../typography/styled";
import Button from "../../button";
import Input from "../../input";
import Select from "../../select";
import Toggle from "../../toggle";
import { clusterSettingsService } from "../../../api";
import { useUi } from "../../../context/ui.context";
import {
  Container,
  HeadWrapper,
  BodyModal,
  Icon,
} from "../create-node-pool/styles";

type ModalProps = {
  closeModalCancel: () => void;
  closeModal: (data?: any) => void;
  dataToEdit?: any;
};

const CreateNetworking: FC<ModalProps> = ({ closeModal, closeModalCancel, dataToEdit }) => {
  const [t] = useTranslation();
  const { viewAlert } = useUi();
  const [loading, setLoading] = useState(false);
  const [loadingComplete, setLoadingComplete] = useState(false);
  const [visible, setVisible] = useState(dataToEdit?.visible ?? true);
  const [kubeProxy, setKubeProxy] = useState(dataToEdit?.kube_proxy ?? false);
  const [addons, setAddons] = useState<any[]>([]);
  const isEdit = !!dataToEdit?.id;

  useEffect(() => {
    clusterSettingsService
      .getAddons({ page_size: 200 })
      .then((res) => {
        const list = (res.data?.objects || []) as any[];
        setAddons(list.map((a) => ({ value: a.id.toString(), label: a.name })));
      })
      .catch(() => {});
  }, []);

  const onSubmit = (values: any) => {
    setLoading(true);
    let securityGroupsRules = null;
    if (values.security_groups_rules) {
      try {
        securityGroupsRules = JSON.parse(values.security_groups_rules);
      } catch {
        viewAlert({ severity: "error", message: t("INVALID_JSON") });
        setLoading(false);
        return;
      }
    }
    const data = {
      addon_id: +values.addon_id,
      visible,
      kube_proxy: kubeProxy,
      security_groups_rules: securityGroupsRules,
    };
    const action = isEdit
      ? clusterSettingsService.updateNetworking(dataToEdit.id, data)
      : clusterSettingsService.createNetworking(data);

    action
      .then(() => {
        setLoadingComplete(true);
        setTimeout(() => {
          closeModal({ success: true });
          setLoading(false);
          setLoadingComplete(false);
        }, 1500);
      })
      .catch((err) => {
        setLoading(false);
        viewAlert({
          severity: "error",
          message: err?.response?.data?.detail || t("ERROR_OCCURRED"),
        });
      });
  };

  const { values, errors, touched, handleSubmit, handleChange, setFieldValue } = useFormik({
    enableReinitialize: true,
    validationSchema: yup.object().shape({
      addon_id: yup.string().required(t("REQUIRED_FIELD")),
    }),
    initialValues: {
      addon_id: dataToEdit?.addon_id?.toString() || "",
      security_groups_rules: dataToEdit?.security_groups_rules
        ? JSON.stringify(dataToEdit.security_groups_rules, null, 2)
        : "",
    },
    onSubmit,
  });

  return (
    <Container style={{ width: "500px" }}>
      <HeadWrapper>
        <HeadLine3 style={{ marginLeft: "10px", marginTop: "10px" }}>
          {isEdit ? t("EDIT_NETWORKING") : t("CREATE_NETWORKING")}
        </HeadLine3>
        <Icon onClick={closeModalCancel} src={icons.CloseIcon} />
      </HeadWrapper>
      <BodyModal>
        <Select title={t("ADDON")} data={addons} selectedValue={values.addon_id} onChange={(val: any) => setFieldValue("addon_id", val?.toString())} fieldName="addon_id" errors={errors} touched={touched} hasSearch required />
        <Toggle title={t("VISIBLE")} value={visible} onChange={() => setVisible(!visible)} />
        <Toggle title={t("KUBE_PROXY")} value={kubeProxy} onChange={() => setKubeProxy(!kubeProxy)} />
        <Input title={t("SECURITY_GROUPS_RULES")} fieldName="security_groups_rules" value={values.security_groups_rules} handleChange={handleChange} errors={errors} touched={touched} isTextArea placeholder='[{"key": "value"}]' />
        <div style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
          <Button variant="stroke" size="display" customStyles={{ width: "177px", justifyContent: "center" }} onClick={closeModalCancel} title={t("CANCEL")} />
          <Button variant="primary" size="display" loading={loading} loadingComplete={loadingComplete} customStyles={{ width: "177px", justifyContent: "center" }} onClick={handleSubmit} title={isEdit ? t("SAVE") : t("CREATE")} />
        </div>
      </BodyModal>
    </Container>
  );
};

export default CreateNetworking;
